import React from "react";

import { ArrowDownwardRounded, HideImageRounded, MenuRounded } from "@mui/icons-material";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import { Box, ButtonBase, Skeleton } from "@mui/material";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import useMainState from "../../../config/context/mainStateContext";
import useModal from "../../../config/context/modalContext";
import { ReportService } from "../../../config/services/ReportService";
import { SpottService } from "../../../config/services/SpottService";
import { GreyPallet } from "../../../config/styles";

import ChipMain from "../../../components/InputMain/ChipMain";
import MainSolidButton from "../../../components/MainSolidButton";
import MainText from "../../../components/MainText";
import PageHeader from "../../../components/PageHeader";

import {
  PostStatusType,
  ReportStatusType,
  ReportType,
  SpottPostType,
} from "../../../types/database";

export default function AdminDashboardPage() {
  const navigate = useNavigate();

  const { setShowSideMenuDrawer } = useMainState();
  const { pushAlert } = useModal();

  const SpottList = SpottService.ListStatusLazy(5, "add");
  const ReportList = ReportService.ListStatus();

  const [postListingStatus, setPostListingStatus] = React.useState<PostStatusType>("pending");
  const [reportListingStatus, setReportListingStatus] = React.useState<ReportStatusType>("pending");

  React.useEffect(() => {
    SpottList.runRequest(postListingStatus, true).catch((err) =>
      pushAlert({ type: "error", message: "Erro ao buscar postagens. " + err.message })
    );
  }, [postListingStatus]);

  React.useEffect(() => {
    ReportList.runRequest(reportListingStatus).catch((err) =>
      pushAlert({ type: "error", message: "Erro ao buscar reports. " + err.message })
    );
  }, [reportListingStatus]);

  return (
    <Box
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
      pt={"30px"}
      pb={"45px"}
    >
      <Box width={"90%"}>
        <PageHeader
          leftIcon={<MenuRounded />}
          onLeftAction={() => setShowSideMenuDrawer(true)}
          title="Dashboard"
          // rightIcon={<DashboardRounded />}
          // onRightAction={() => setShowSideMenuDrawer(true)}
        />

        {/* Posts */}

        <Box mb={4}>
          <Box mb={2}>
            <MainText size="subtitle" color="black">
              Postagens
            </MainText>
          </Box>

          <Box mb={4} gap={1} display={"flex"}>
            <ChipMain
              options={[
                { name: "Pendentes", value: "pending" },
                { name: "Rejeitadas", value: "rejected" },
                { name: "Aprovadas", value: "approved" },
              ]}
              onChange={(value) => setPostListingStatus(value as PostStatusType)}
              value={postListingStatus}
            />
          </Box>

          {SpottList.isLoading && SpottList.items?.length === 0 ? (
            <>
              <Box mt={2}>
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={"100%"}
                  height={80}
                  sx={{ borderRadius: "15px", margin: "0px 4px" }}
                />
              </Box>
              <Box my={2}>
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={"100%"}
                  height={80}
                  sx={{ borderRadius: "15px", margin: "0px 4px" }}
                />
              </Box>
            </>
          ) : SpottList.items?.length ? (
            SpottList.items.map((item: SpottPostType) => (
              <ButtonBase
                key={item.id}
                sx={{ mb: 1 }}
                onClick={() => navigate("/post/" + item.id)}
                style={{
                  border: `3px solid ${GreyPallet.Light}`,
                  borderRadius: 15,
                  width: "100%",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <Box
                  style={{
                    textAlign: "start",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  px={2}
                  py={1}
                >
                  <Box
                    mb={1}
                    style={{
                      textAlign: "start",
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <MainText size="small" color="regular">
                      {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                    </MainText>

                    {item.files.length > 0 &&
                      (item.files?.[0].deleted ? (
                        <HideImageRounded style={{ fontSize: 15, color: GreyPallet.Regular }} />
                      ) : (
                        <ImageRoundedIcon style={{ fontSize: 15, color: GreyPallet.Regular }} />
                      ))}
                  </Box>

                  <MainText size="caption" color="black">
                    {item.text}
                  </MainText>
                </Box>
              </ButtonBase>
            ))
          ) : (
            <MainText size="caption" color="regular">
              Nenhuma nova postagem...
            </MainText>
          )}

          {SpottList.hasMore && (
            <MainSolidButton
              text="Carregar Mais"
              icon={<ArrowDownwardRounded />}
              loading={SpottList.isLoading}
              onClick={() =>
                SpottList.runRequest(postListingStatus).catch((err) =>
                  pushAlert({ type: "error", message: "Erro ao buscar postagens. " + err.message })
                )
              }
            />
          )}
        </Box>

        {/* Reports */}

        <Box my={4}>
          <Box mb={2}>
            <MainText size="subtitle" color="danger">
              Denúncias
            </MainText>
          </Box>

          <Box mb={4} gap={1} display={"flex"}>
            <ChipMain
              options={[
                { name: "Pendentes", value: "pending" },
                { name: "Rejeitadas", value: "rejected" },
                { name: "Aprovadas", value: "approved" },
              ]}
              onChange={(value) => setReportListingStatus(value as PostStatusType)}
              value={reportListingStatus}
            />
          </Box>

          {ReportList.isLoading ? (
            <>
              <Box mt={2}>
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={"100%"}
                  height={80}
                  sx={{ borderRadius: "15px", margin: "0px 4px" }}
                />
              </Box>
              <Box mt={2}>
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  width={"100%"}
                  height={80}
                  sx={{ borderRadius: "15px", margin: "0px 4px" }}
                />
              </Box>
            </>
          ) : ReportList.items?.length ? (
            ReportList.items.map((item: ReportType) => (
              <ButtonBase
                key={item.id}
                sx={{ mb: 1 }}
                onClick={() => navigate("/report/" + item.id)}
                style={{
                  border: `3px solid ${GreyPallet.Light}`,
                  borderRadius: 15,
                  width: "100%",
                  overflow: "hidden",
                  marginBottom: 20,
                }}
              >
                <Box
                  style={{
                    textAlign: "start",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  px={2}
                  py={1}
                >
                  <Box
                    mb={1}
                    style={{
                      textAlign: "start",
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <MainText size="small" color="regular">
                      {DateTime.fromMillis(item.createdAtTimestamp).toFormat("dd/LL/y HH:mm")}
                    </MainText>
                  </Box>

                  <MainText size="caption" color="black">
                    {item.text}
                  </MainText>
                </Box>
              </ButtonBase>
            ))
          ) : (
            <MainText size="caption" color="regular">
              Nenhuma nova denúncia...
            </MainText>
          )}
        </Box>
      </Box>
    </Box>
  );
}
